import { MathObject } from '../mathObjects/MathObject';
import { SlashedObject } from '../mathObjects/SlashedObject';
import { Text } from '../mathObjects/Text';
import { ObjectFormat } from '../uiObjects/objectFormat';

import { MINUS } from './decodeText';

type SpanAttributeValue = string | boolean;
type WritableObjectFormat = Record<string, SpanAttributeValue | undefined>;

export const extractSpanAttributes = (tagContents: string): ObjectFormat => {
  const format = new ObjectFormat();

  // remove the 'span' tag name
  const attributePairs = tagContents.replace(/^\S+\s+/, '');

  // Match key=value pairs where:
  // - key is \w+
  // - value is any sequence of non-space, non-> characters
  const attrRegex = /(\w+)=([^>\s]+)/g;

  for (const match of attributePairs.matchAll(attrRegex)) {
    const key = match[1];
    let value = match[2];

    if (Object.prototype.hasOwnProperty.call(format, key)) {
      // convert ASCII hyphen
      if (key === 'decorator' && value === '-') {
        value = MINUS;
      }

      const val = value === 'true' ? true : value === 'false' ? false : value;

      ((format as unknown) as WritableObjectFormat)[key] = val;
    }
  }

  return format;
};

export const applySpanFormatToDirectChild = (
  child: MathObject,
  format: ObjectFormat
): MathObject => {
  const childFormat =
    format.slash === undefined ? format : formatWithoutSlash(format);

  if (childFormat) {
    applyFormatRecursive(child, childFormat);
  }

  if (shouldWrapWithSlash(child, format)) {
    return new SlashedObject(child, child.getObjectFormat());
  }

  return child;
};

const hasFormatValue = (format: ObjectFormat | undefined): boolean =>
  format !== undefined &&
  (format.color !== undefined ||
    format.bold !== undefined ||
    format.italic !== undefined ||
    format.id !== undefined ||
    format.parentId !== undefined ||
    format.decorator !== undefined ||
    format.slash !== undefined);

const formatWithoutSlash = (format: ObjectFormat): ObjectFormat | undefined => {
  const next = new ObjectFormat(
    format.color,
    format.bold,
    format.italic,
    format.id,
    format.parentId,
    format.decorator
  );

  return hasFormatValue(next) ? next : undefined;
};

const shouldWrapWithSlash = (
  child: MathObject,
  format: ObjectFormat
): boolean =>
  format.slash === true &&
  !child.isSlashedObject() &&
  !(child instanceof Text && child.text.trim().length === 0);

const applyFormatRecursive = (obj: MathObject, format: ObjectFormat): void => {
  const before = obj.getObjectFormat?.();
  const merged = ObjectFormat.merge(format, before);

  obj.setObjectFormat(merged);

  // If font-affecting attributes changed, re-measure Text now so layout stays consistent.
  if (obj instanceof Text && merged) {
    const beforeBold = before?.bold;
    const beforeItalic = before?.italic;

    const afterBold = merged.bold;
    const afterItalic = merged.italic;

    if (beforeBold !== afterBold || beforeItalic !== afterItalic) {
      obj.sizeMe(true); // dontTellParent=true (we'll do a full layout after parse anyway)
    }
  }

  for (const child of obj.children ?? []) {
    applyFormatRecursive(child as MathObject, format);
  }
};
